import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import minMax from 'dayjs/plugin/minMax';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import abbreviations from './timeZoneAbbreviations.json';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(localeData);
dayjs.extend(minMax);

const abbreviationsByZone: Record<string, Record<string, string>> = abbreviations;
const timeZoneAbbreviations = new Map(
  Object.entries(abbreviationsByZone).map(([zone, byOffset]) => [
    zone,
    new Map(Object.entries(byOffset))
  ])
);
const originalOffsetName = dayjs.prototype.offsetName;

dayjs.prototype.offsetName = function offsetName(type?: 'short' | 'long') {
  const details = '$x' in this ? this.$x : undefined;
  const zone =
    details &&
    typeof details === 'object' &&
    '$timezone' in details &&
    typeof details.$timezone === 'string'
      ? details.$timezone
      : undefined;
  if (!zone || type === 'long') return originalOffsetName.call(this, type);

  const offset = this.utcOffset();
  const abbreviation = timeZoneAbbreviations.get(zone)?.get(String(offset));
  if (abbreviation) return abbreviation;

  const minutes = Math.abs(offset);
  const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
  const remainder = minutes % 60;
  return `${offset < 0 ? '-' : '+'}${hours}${remainder ? String(remainder).padStart(2, '0') : ''}`;
};

export const normalizeTimeZone = (timeZone?: string | null): string =>
  !timeZone || timeZone === 'Etc/Unknown' ? 'UTC' : timeZone;

export const safeTimeZone = (timeZone: string): string => {
  try {
    Intl.DateTimeFormat('en-US', { timeZone });
    return timeZone;
  } catch {
    return 'UTC';
  }
};

// Some Windows setups report Etc/Unknown, which Intl rejects. MUI's AdapterDayjs and
// dayjs itself call tz.guess() directly, so sanitise it at the source.
const rawGuess = dayjs.tz.guess;
dayjs.tz.guess = () => safeTimeZone(normalizeTimeZone(rawGuess()));

export const guessBrowserTimeZone = (): string => dayjs.tz.guess();

export const browserTimeZone = guessBrowserTimeZone();

export const getSavedUserTimeZone = (): string | null => {
  if (typeof document === 'undefined') return null;

  const timeZone = document.querySelector('meta[name="user-timezone"]')?.getAttribute('value');
  return timeZone ? normalizeTimeZone(timeZone) : null;
};

export const resolveTimeZone = (userTimeZone?: string | null): string => {
  const explicitTimeZone =
    typeof userTimeZone === 'string' && userTimeZone.trim() !== '' ? userTimeZone : null;

  return normalizeTimeZone(explicitTimeZone ?? getSavedUserTimeZone() ?? browserTimeZone);
};

// NOTE: This is resolved once at module load time. Mid-session timezone changes
// require a page reload before helpers that depend on preferredTimeZone reflect them.
export const preferredTimeZone = resolveTimeZone();

export default dayjs;
